/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { useTrapReceiveForm } from "./hooks";
import { getProtocolList } from "../../helpers";
import { AuthenticationOptions, PrivacyOptions, constStr } from "../../../../globals/constants";
import { selectedTrapSelector } from "../../../../store/api/admin/selectors";
import { handleTrapDataChecked, handleTrapDataUnchecked } from "../helpers";
import { useSelector } from "react-redux";
import ConfirmDialog from "../../../../components/Shared/Dialogs/ConfirmDialog";
import { DIALOGOPENFOR } from "../../../../globals/globals";
import { confirmDialogSelector, errorMsgSelector } from "../../../../store/helpers/helperSelectors";
import { InputField } from "../../../../components/Shared/Radix/Inputs/InputField";
import { PasswordField } from "../../../../components/Shared/Radix/Inputs/PasswordField";
import { Label } from "../../../../components/ui/label";
import { Button } from "../../../../components/ui/button";
import { Checkbox } from "../../../../components/ui/checkbox";
import { RadixSelectControl } from "../../../../components/ui/radix-select";
import { ShadcnHeading } from "../../../../components/ui/shadcn-heading";
import { setErrorMsg } from "../../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../../store/hooks";
import { store } from "../../../../store";
import { isValidPassword } from "../../../../utils/validations";

interface IStateKey {
  [key: string]: any;
}

const TrapReceive: React.FC = () => {
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const trapReceiveDetails = useSelector(selectedTrapSelector);
  const storeErrMsg = useSelector(errorMsgSelector);
  const dispatch = useStoreDispatch();
  const {
    trapReceiveData,
    setTrapReceiveData,
    onBlurHandler,
    handleResetTrapRecieve,
    submitHandler,
    isAuthChecked,
    setIsAuthChecked,
    isPrivChecked,
    setIsPrivChecked,
    snmpv1v2,
    setSnmpv1v2,
    snmpv3,
    setSnmpv3,
    trapAuthConfirmPassword,
    setTrapAuthConfirmPassword,
    trapAuthPassword,
    setTrapAuthPassword,
    trapPrivConfirmPassword,
    setTrapPrivConfirmPassword,
    trapPrivPassword,
    setTrapPrivPassword,
    authConfirmPassword,
    setAuthConfirmPassword,
    privConfirmPassword,
    setPrivConfirmPassword,
    isAuthProtoChecked,
    setIsAuthProtoCehcked,
    isPrivProtoChecked,
    setIsPrivProtoCehcked,
    isFilterTrap,
    setIsFilterTrap,
    authPassword,
    setAuthPassword,
    privPassword,
    setPrivPassword,
    handleMsg,
    handleSelectVersion,
  } = useTrapReceiveForm();

  const onFieldChange = (name: string, value: string) => {
    setTrapReceiveData({ ...trapReceiveData, [name]: value });
    dispatch(setErrorMsg({ ...store.getState().helpers.errorMsg, [name]: "" }));
  };

  const handleSnmpv1v2Checkbox = (checked: boolean) => {
    setSnmpv1v2(checked);
    if (checked) {
      const newData: any = handleTrapDataChecked("snmpv1v2", trapReceiveData, trapReceiveDetails);
      setTrapReceiveData(newData);
      trapReceiveDetails?.community && setIsFilterTrap(true);
    } else if (!checked && snmpv3) {
      const newData: any = handleTrapDataUnchecked("snmpv1v2", trapReceiveData, trapReceiveDetails);
      setTrapReceiveData(newData);
      setIsFilterTrap(false);
    }
  };

  const handleSnmpv3Checkbox = (checked: boolean) => {
    setSnmpv3(checked);
    if (checked) {
      const newData: any = handleTrapDataChecked("snmpv3", trapReceiveData, trapReceiveDetails);
      setTrapReceiveData(newData);
    } else {
      const newData: any = handleTrapDataUnchecked("snmpv3", trapReceiveData, trapReceiveDetails);
      setTrapReceiveData(newData);
      const existing = store.getState().helpers.errorMsg;
      dispatch(setErrorMsg({ ...existing, engineId: "" }));
    }
  };

  const handleFilterTrapCheckbox = (checked: boolean) => {
    setIsFilterTrap(checked);
    if (!checked) {
      setTrapReceiveData({ ...trapReceiveData, community: "" });
    }
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setTrapReceiveData({ ...trapReceiveData, [name]: value });
    dispatch(setErrorMsg({ ...store.getState().helpers.errorMsg, [name]: "" }));
  };

  const onPassChange = (name: string, value: string) => {
    const existing = store.getState().helpers.errorMsg;
    if (name === "authPassword") {
      setAuthPassword(value);
      if (value) {
        const isValidPass = isValidPassword(value);
        dispatch(setErrorMsg({
          ...existing,
          authPassword: isValidPass ? "" : "Password is invalid. It must meet the specified criteria!",
        }));
      } else {
        dispatch(setErrorMsg({ ...existing, authPassword: "" }));
      }
    } else if (name === "privPassword") {
      setPrivPassword(value);
      if (value) {
        const isValidPass = isValidPassword(value);
        dispatch(setErrorMsg({
          ...existing,
          privPassword: isValidPass ? "" : "Password is invalid. It must meet the specified criteria!",
        }));
      } else {
        dispatch(setErrorMsg({ ...existing, privPassword: "" }));
      }
    } else if (name === "authConfirmPassword") {
      setAuthConfirmPassword(value);
      dispatch(setErrorMsg({
        ...existing,
        authConfirmPassword: value !== authPassword ? "Passwords do not match" : "",
      }));
    } else if (name === "privConfirmPassword") {
      setPrivConfirmPassword(value);
      dispatch(setErrorMsg({
        ...existing,
        privConfirmPassword: value !== privPassword ? "Passwords do not match" : "",
      }));
    } else if (name === "trapAuthPassword") {
      setTrapAuthPassword(value);
      if (value) {
        const isValidPass = isValidPassword(value);
        dispatch(setErrorMsg({
          ...existing,
          trapAuthPassword: isValidPass ? "" : "Password is invalid. It must meet the specified criteria!",
        }));
      } else {
        dispatch(setErrorMsg({ ...existing, trapAuthPassword: "" }));
      }
    } else if (name === "trapPrivPassword") {
      setTrapPrivPassword(value);
      if (value) {
        const isValidPass = isValidPassword(value);
        dispatch(setErrorMsg({
          ...existing,
          trapPrivPassword: isValidPass ? "" : "Password is invalid. It must meet the specified criteria!",
        }));
      } else {
        dispatch(setErrorMsg({ ...existing, trapPrivPassword: "" }));
      }
    } else if (name === "trapAuthConfirmPassword") {
      setTrapAuthConfirmPassword(value);
      dispatch(setErrorMsg({
        ...existing,
        trapAuthConfirmPassword: value !== trapAuthPassword ? "Passwords do not match" : "",
      }));
    } else if (name === "trapPrivConfirmPassword") {
      setTrapPrivConfirmPassword(value);
      dispatch(setErrorMsg({
        ...existing,
        trapPrivConfirmPassword: value !== trapPrivPassword ? "Passwords do not match" : "",
      }));
    }
  };

  const isTrapDataUpdated = (initialState: IStateKey, currentState: IStateKey) => {
    const initialKeys: any = Object.keys(initialState);
    let isValidTrap: boolean = true;

    for (const key of initialKeys) {
      if (key === "authPassword" || key === "privPassword") {
        continue;
      }
      if (initialState[key] !== currentState[key]) {
        isValidTrap = false;
      }
    }
    // If the main SNMPv3 authentication / privacy passwords differ from what is stored,
    // then data has been updated.
    if (authPassword !== trapReceiveData?.authPassword || privPassword !== trapReceiveData?.privacyPassword) {
      isValidTrap = false;
    }

    if (
      trapAuthPassword ||
      trapPrivPassword ||
      trapAuthConfirmPassword ||
      trapPrivConfirmPassword
    ) {
      isValidTrap = false;
    }

    const hasAnyError = Object.values(storeErrMsg || {}).some(
      (msg: any) => msg && String(msg).trim() !== ""
    );

    if (hasAnyError) {
      return true; // disable Save
    }

    return isValidTrap;
  };

  return (
    <div
      className="flex flex-col w-full overflow-y-auto px-4 md:px-4 pt-1 pb-10"
      style={{ color: "var(--foreground)" }}
    >
      {openConfirmDialog === DIALOGOPENFOR.TRAPRECEIVE && (
        <ConfirmDialog actionName={constStr.ok} handleClickAction={handleSelectVersion}>
          {handleMsg()}
        </ConfirmDialog>
      )}

      {/* Form Boundary */}
      <div
        style={{
          border: "1px solid var(--color-border)",
          borderRadius: "var(--radius)",
          padding: "24px",
          boxShadow: "var(--shadow-card)",
          backgroundColor: "var(--background)",
        }}
      >
        <form autoComplete="off">
          <div className="space-y-5 max-w-lg">
            {/* SNMP V1/V2 */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="snmpv1v2"
                checked={snmpv1v2}
                onCheckedChange={(checked) => handleSnmpv1v2Checkbox(!!checked)}
                className="m-0"
              />
              <Label htmlFor="snmpv1v2" className="cursor-pointer">
                SNMP V1/V2
              </Label>
            </div>

            {/* Filter by Trap Community */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="isFilterTrap"
                disabled={!snmpv1v2}
                checked={isFilterTrap}
                onCheckedChange={(checked) => handleFilterTrapCheckbox(!!checked)}
                className="m-0"
              />
              <Label htmlFor="isFilterTrap" className="cursor-pointer">
                Filter by Trap Community
              </Label>
            </div>

            {/* Trap Community */}
            <InputField
              name="community"
              label="Trap Community"
              disabled={!isFilterTrap}
              value={trapReceiveData?.community || ""}
              onChange={onFieldChange}
              onBlur={onBlurHandler}
              maxLength={512}
              tooltipText="Maximum length: 512 characters"
              placeholder="Enter trap community"
            />

            {/* SNMP V3 */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="snmpv3"
                checked={snmpv3}
                onCheckedChange={(checked) => handleSnmpv3Checkbox(!!checked)}
                className="m-0"
              />
              <Label htmlFor="snmpv3" className="cursor-pointer">
                SNMP V3
              </Label>
            </div>

            {/* Engine ID */}
            <InputField
              name="engineId"
              label="Engine ID"
              disabled={!snmpv3}
              value={trapReceiveData?.engineId || ""}
              onChange={onFieldChange}
              onBlur={onBlurHandler}
              maxLength={512}
              tooltipText="Maximum length: 512 characters"
              placeholder="Enter engine id"
            />

            {/* User Name */}
            <InputField
              name="userName"
              label="User Name"
              disabled={!snmpv3}
              value={trapReceiveData?.userName || ""}
              onChange={onFieldChange}
              onBlur={onBlurHandler}
              maxLength={512}
              tooltipText="Maximum length: 512 characters"
              placeholder="Enter user name"
            />

            {/* Parameters to process SNMPV3 Inform request */}
            <div className="flex items-center gap-2">
              <Label htmlFor={"regionAccess"} className="text-sm font-medium" style={{ color: "var(--muted-foreground)" }}>
                Parameters to process SNMPV3 Inform request:
              </Label>
            </div>

            {/* Authentication Protocol */}
            <div className="flex flex-col gap-1">
              <Label htmlFor="authProtocol" className="text-sm font-medium mb-3">Authentication Protocol</Label>
              <RadixSelectControl
                disabled={!snmpv3}
                value={trapReceiveData?.authProtocol?.toUpperCase() || ""}
                options={getProtocolList(AuthenticationOptions).map((item: any) => ({ value: item.value, label: item.text }))}
                onValueChange={(value) => handleSelectChange({ target: { name: "authProtocol", value } } as React.ChangeEvent<HTMLSelectElement>)}
              />
              {storeErrMsg?.authProtocol && <p className="text-xs text-destructive m-0">{storeErrMsg.authProtocol}</p>}
            </div>

            {/* Change Password Authentication Protocol */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="isAuthChecked"
                checked={isAuthChecked}
                onCheckedChange={(checked) => {
                  setIsAuthChecked(!!checked);
                  if (!checked) {
                    const existing = store.getState().helpers.errorMsg;
                    dispatch(setErrorMsg({ ...existing, authPassword: "", authConfirmPassword: "" }));
                  }
                }}
                className="m-0"
              />
              <Label htmlFor="isAuthChecked" className="cursor-pointer">
                Change Password Authentication Protocol
              </Label>
            </div>

            {isAuthChecked && (
              <>
                <PasswordField
                  label="Password"
                  name="authPassword"
                  disabled={trapReceiveData?.authProtocol?.toLowerCase() === "no_auth"}
                  value={authPassword}
                  onChange={onPassChange}
                  onBlur={onBlurHandler}
                  maxLength={128}
                  placeholder="Enter password"
                />
                <PasswordField
                  label="Confirm Password"
                  name="authConfirmPassword"
                  disabled={trapReceiveData?.authProtocol?.toLowerCase() === "no_auth"}
                  value={authConfirmPassword}
                  onChange={onPassChange}
                  onBlur={onBlurHandler}
                  maxLength={128}
                  placeholder="Confirm password"
                />
              </>
            )}

            {/* Privacy Protocol */}
            <div className="flex flex-col gap-1">
              <Label htmlFor="privProtocol" className="text-sm font-medium mb-3">Privacy Protocol</Label>
              <RadixSelectControl
                disabled={!snmpv3}
                value={trapReceiveData?.privProtocol?.toUpperCase() || ""}
                options={getProtocolList(PrivacyOptions).map((item: any) => ({ value: item.value, label: item.text }))}
                onValueChange={(value) => handleSelectChange({ target: { name: "privProtocol", value } } as React.ChangeEvent<HTMLSelectElement>)}
              />
              {storeErrMsg?.privProtocol && <p className="text-xs text-destructive m-0">{storeErrMsg.privProtocol}</p>}
            </div>

            {/* Change Password Privacy Protocol */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="isPrivChecked"
                checked={isPrivChecked}
                onCheckedChange={(checked) => {
                  setIsPrivChecked(!!checked);
                  if (!checked) {
                    const existing = store.getState().helpers.errorMsg;
                    dispatch(setErrorMsg({ ...existing, privPassword: "", privConfirmPassword: "" }));
                  }
                }}
                className="m-0"
              />
              <Label htmlFor="isPrivChecked" className="cursor-pointer">
                Change Password Privacy Protocol
              </Label>
            </div>

            {isPrivChecked && (
              <>
                <PasswordField
                  label="Password"
                  name="privPassword"
                  disabled={trapReceiveData?.privProtocol?.toLowerCase() === "no_priv"}
                  value={privPassword}
                  onChange={onPassChange}
                  onBlur={onBlurHandler}
                  maxLength={128}
                  placeholder="Enter password"
                />
                <PasswordField
                  label="Confirm Password"
                  name="privConfirmPassword"
                  disabled={trapReceiveData?.privProtocol?.toLowerCase() === "no_priv"}
                  value={privConfirmPassword}
                  onChange={onPassChange}
                  onBlur={onBlurHandler}
                  maxLength={128}
                  placeholder="Confirm password"
                />
              </>
            )}

            {/* Parameters to process SNMPV3 TRAP */}
            <div className="flex items-center gap-2">
              <Label htmlFor={"regionAccess"} className="text-sm font-medium" style={{ color: "var(--muted-foreground)" }}>
                Parameters to process SNMPV3 TRAP:
              </Label>
            </div>


            {/* Change Trap AuthProtocol Password */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="isAuthProtoChecked"
                checked={isAuthProtoChecked}
                onCheckedChange={(checked) => {
                  setIsAuthProtoCehcked(!!checked);
                  if (!checked) {
                    const existing = store.getState().helpers.errorMsg;
                    dispatch(setErrorMsg({ ...existing, trapAuthPassword: "", trapAuthConfirmPassword: "" }));
                  }
                }}
                className="m-0"
              />
              <Label htmlFor="isAuthProtoChecked" className="cursor-pointer">
                Change Trap AuthProtocol Password
              </Label>
            </div>

            {isAuthProtoChecked && (
              <>
                <PasswordField
                  label="Password"
                  name="trapAuthPassword"
                  disabled={!isAuthProtoChecked}
                  value={trapAuthPassword}
                  onChange={onPassChange}
                  onBlur={onBlurHandler}
                  maxLength={128}
                  placeholder="Enter password"
                />
                <PasswordField
                  label="Confirm Password"
                  name="trapAuthConfirmPassword"
                  disabled={!isAuthProtoChecked}
                  value={trapAuthConfirmPassword}
                  onChange={onPassChange}
                  onBlur={onBlurHandler}
                  maxLength={128}
                  placeholder="Confirm password"
                />
              </>
            )}

            {/* Change Trap Privacy Protocol Password */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="isPrivProtoChecked"
                checked={isPrivProtoChecked}
                onCheckedChange={(checked) => {
                  setIsPrivProtoCehcked(!!checked);
                  if (!checked) {
                    const existing = store.getState().helpers.errorMsg;
                    dispatch(setErrorMsg({ ...existing, trapPrivPassword: "", trapPrivConfirmPassword: "" }));
                  }
                }}
                className="m-0"
              />
              <Label htmlFor="isPrivProtoChecked" className="cursor-pointer">
                Change Trap Privacy Protocol Password
              </Label>
            </div>

            {isPrivProtoChecked && (
              <>
                <PasswordField
                  label="Password"
                  name="trapPrivPassword"
                  disabled={!isPrivProtoChecked}
                  value={trapPrivPassword}
                  onChange={onPassChange}
                  onBlur={onBlurHandler}
                  maxLength={128}
                  placeholder="Enter password"
                />
                <PasswordField
                  label="Confirm Password"
                  name="trapPrivConfirmPassword"
                  disabled={!isPrivProtoChecked}
                  value={trapPrivConfirmPassword}
                  onChange={onPassChange}
                  onBlur={onBlurHandler}
                  maxLength={128}
                  placeholder="Confirm password"
                />
              </>
            )}
            {/* Action Buttons */}
            <div className="flex justify-end gap-2 mt-4">
              <Button type="button" variant="outline" onClick={handleResetTrapRecieve}>
                Reset
              </Button>
              <Button type="button" disabled={isTrapDataUpdated(trapReceiveDetails, trapReceiveData)} onClick={submitHandler}>
                Save
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TrapReceive;
